import React, { useMemo } from 'react'
import styled from 'styled-components'
import { rem } from 'polished'
import AlwaysOnRoomOption from './AlwaysOnRoomOption'
import LayoutContainer from 'components/Common/LayoutContainer'
import { mediaQueryUp } from 'components/utils/breakpoint'

import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import { getDurationPackageOptions, OfferPackageOptionView } from 'selectors/offerPageSelectors'
import Caption from 'components/Luxkit/Typography/Caption'
import MessageBanner from 'components/Luxkit/Banners/MessageBanner'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import { buildRoomPackageOptionsViewOrder } from 'lib/offerPageUtil'
import { OfferPageRoomOption } from 'contexts/OfferPage/offerPageStateReducer'
import config from 'constants/config'
import { getOfferExtra } from 'selectors/offerExtraSelector'
import { getSupportContactNumber } from 'selectors/supportContactSelector'
import moment from 'moment'
import { ISO_DATE_FORMAT } from 'constants/dateFormats'
import useOfferAvailableRates from 'hooks/useOfferAvailableRates'

const PackageSelectionWrapper = styled(VerticalSpacer)`
  ${mediaQueryUp.sm} {
    position: relative;
    padding-right: ${rem(20)};
    padding-left: ${rem(20)};
  }
`

const PackageOptionsGrid = styled(LayoutContainer)`
  padding: 0;

  > * + * {
    margin-top: ${rem(16)};
  }
`

interface Props {
  offer: App.Offer;
  offerExtra?: App.OfferExtra;
  rooms: Array<App.Occupants>;
  checkIn?: moment.Moment;
  packageOptions: Array<OfferPackageOptionView>;
  airport?: App.Airport;
  roomOption?: OfferPageRoomOption;
  showFlightInformation?: boolean;
  destinationAirport?: App.Airport;
  onBookNowInvalid?: () => void;
  onViewPackageOptions: () => void;
  supportPhoneNumber?: string;
}

function AlwaysOnRoomOptions(props: Props) {
  const {
    offer,
    offerExtra,
    packageOptions,
    showFlightInformation,
    destinationAirport,
    roomOption,
    onViewPackageOptions,
    checkIn,
    rooms,
    supportPhoneNumber,
  } = props

  const hasRoomOption = roomOption && Object.entries(roomOption).length === 2
  const packageOptionsByRoom = useMemo(() => {
    return buildRoomPackageOptionsViewOrder(packageOptions)
  }, [packageOptions])

  const bookedTodayByRoom = useMemo(() => {
    return offerExtra?.roomsPurchasesToday?.reduce((acc, room) => {
      acc[room.roomTypeId] = room.purchases
      return acc
    }, {}) || {}
  }, [offerExtra])

  const highlightedPackage = hasRoomOption ?
      {
        roomId: roomOption?.roomTypeId,
        packageViews: packageOptionsByRoom.find(item => item.roomId === roomOption?.roomTypeId)?.packageViews ?? [],
      } : null

  const checkOut = checkIn ? checkIn.clone().add(packageOptionsByRoom?.[0]?.packageViews?.[0]?.package?.duration, 'days') : undefined

  const offerAvailableRates = useOfferAvailableRates(offer?.id, {
    checkIn: checkIn?.format(ISO_DATE_FORMAT),
    checkOut: checkOut?.format(ISO_DATE_FORMAT),
    rooms,
  })

  const hasRateError = !!offerAvailableRates?.error

  return (<PackageOptionsGrid>
    {hasRateError && <MessageBanner
      kind="warning"
      desktopAlign="center"
      heading={`The third party booking system for ${offer.property?.name} is currently down`}
      description={`Please call our VIP team on ${supportPhoneNumber} who are ready to provide assistance to complete your booking`}
    />}
    {!hasRateError && <PackageSelectionWrapper gap={20}>
      {hasRoomOption && highlightedPackage.packageViews.length > 0 && <AlwaysOnRoomOption
        key={highlightedPackage.roomId}
        offer={offer}
        bookedToday={bookedTodayByRoom[highlightedPackage.roomId]}
        packageViews={highlightedPackage.packageViews}
        flightDestination={destinationAirport}
        isHighlighted
        onViewPackageOptions={onViewPackageOptions}
      />}
      {packageOptionsByRoom.map(item => {
        if (hasRoomOption && roomOption.roomTypeId === item.roomId) {
          return null
        }
        return (<AlwaysOnRoomOption
          key={item.roomId}
          offer={offer}
          bookedToday={bookedTodayByRoom[item.roomId]}
          packageViews={item.packageViews}
          flightDestination={destinationAirport}
          onViewPackageOptions={onViewPackageOptions}
        />)
      })}
    </PackageSelectionWrapper>}
    {showFlightInformation &&
      <Caption variant="medium" align="center">
        *Price is indicative and based on sample return fares (inclusive of booking fees on Low Cost Carriers). To view live pricing, conduct a full search. {config.brandConfig.name} full Terms and Conditions apply.
      </Caption>
    }
  </PackageOptionsGrid>
  )
}

export default withOfferPageAndReduxState(
  (state, appState) => ({
    offer: state.offer,
    offerExtra: getOfferExtra(appState, state.offer.id),
    checkIn: state.checkInDate,
    rooms: state.rooms,
    airport: state.airport,
    roomOption: state.roomOption,
    packageOptions: getDurationPackageOptions(state, appState),
    supportPhoneNumber: getSupportContactNumber(appState),
  }),
)(AlwaysOnRoomOptions)
